import { formatBR } from '@control-center/shared';

import { PillButton } from '../../../components/clients/PillButton';
import { closedEndLabel } from './Step3Details';
import type { WizardData } from './Wizard';

interface Step4ConfirmProps {
  data: WizardData;
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: () => void | Promise<void>;
  onCancel: () => void;
}

function formatBRL(value: string | number): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return 'R$ —';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const CYCLE_LABEL: Record<string, string> = {
  mensal: 'Mensal',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
  pagamento_unico: 'Pagamento único',
};

const PAYMENT_LABEL: Record<string, string> = {
  pix: 'Pix',
  boleto: 'Boleto',
  cartao: 'Cartão',
  transferencia: 'Transferência',
  cobranca_manual: 'Cobrança manual',
};

export function Step4Confirm({
  data,
  isSubmitting,
  onBack,
  onSubmit,
  onCancel,
}: Step4ConfirmProps): JSX.Element {
  const { client, plan } = data;
  const trialBadge = plan && plan.trialDays > 0 ? `${plan.trialDays}d de trial` : null;

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h2 className="m-0 text-[15px] font-semibold text-ink">Confirme os dados</h2>
        <p className="text-[12px] text-muted">
          {trialBadge
            ? `Status inicial será em_teste — ${trialBadge}.`
            : data.auto_renew
              ? 'Status inicial será ativa.'
              : 'Status inicial: ativa até o fim do período (cancelamento já agendado).'}
        </p>
      </div>

      <section>
        <h3 className="m-0 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          Cliente
        </h3>
        <div className="mt-2 rounded-[10px] border border-line bg-panel-2 p-3 text-[13px]">
          <div className="font-medium text-ink">{client?.name ?? '—'}</div>
          <div className="text-[11px] text-muted-2">
            {client?.document ?? 'sem documento'}
            {client?.email ? ` · ${client.email}` : ''}
          </div>
        </div>
      </section>

      <section>
        <h3 className="m-0 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          Plano
        </h3>
        <div className="mt-2 rounded-[10px] border border-line bg-panel-2 p-3 text-[13px]">
          <div className="flex items-center justify-between gap-3">
            <div className="font-medium text-ink">{plan?.name ?? '—'}</div>
            {trialBadge && (
              <span className="rounded-full bg-warn/15 px-2 py-[1px] text-[10px] font-medium text-warn">
                {trialBadge}
              </span>
            )}
          </div>
          <div className="mt-1 text-[11px] text-muted-2">
            {plan ? formatBRL(plan.monthlyPrice) : '—'} por mês · {plan?._count.entitlements ?? 0}{' '}
            entitlement
            {plan?._count.entitlements === 1 ? '' : 's'}
          </div>
        </div>
      </section>

      <section>
        <h3 className="m-0 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          Detalhes
        </h3>
        <dl className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Item label="Início" value={formatBR(data.start_date)} />
          <Item label="Ciclo" value={CYCLE_LABEL[data.billing_cycle] ?? data.billing_cycle} />
          <Item
            label="Pagamento"
            value={
              data.payment_method
                ? (PAYMENT_LABEL[data.payment_method] ?? data.payment_method)
                : '—'
            }
          />
          <Item label="Auto-renew" value={data.auto_renew ? 'Sim' : 'Não'} />
        </dl>
        {!data.auto_renew && (
          <p className="mt-3 rounded-[8px] border border-warn/40 bg-warn/10 p-3 text-[12px] text-warn">
            {closedEndLabel(data)}
          </p>
        )}
        {data.observations.trim() !== '' && (
          <p className="mt-3 rounded-[8px] bg-panel-2 p-3 text-[12px] text-muted">
            {data.observations}
          </p>
        )}
      </section>

      <div className="flex items-center justify-between gap-2 border-t border-line pt-4">
        <PillButton type="button" onClick={onBack} disabled={isSubmitting}>
          ← Voltar
        </PillButton>
        <div className="flex items-center gap-2">
          <PillButton type="button" onClick={onCancel} disabled={isSubmitting}>
            Cancelar
          </PillButton>
          <PillButton solid type="button" onClick={() => void onSubmit()} disabled={isSubmitting}>
            {isSubmitting ? 'Criando…' : 'Criar assinatura'}
          </PillButton>
        </div>
      </div>
    </div>
  );
}

function Item({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-3">{label}</dt>
      <dd className="mt-1 text-[13px] text-ink">{value}</dd>
    </div>
  );
}
